/**
 * Shared layout and typography aligned with `invite_user.hbs` and partials.
 */

/** Outer + inner body table shell from invite_user.hbs */
export const EMAIL_BODY_SHELL = {
  prefix: `<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:#eef2f7;">
  <tr>
    <td align="center" style="padding:0 16px 0;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600"
        style="max-width:600px;width:100%;background-color:#ffffff;">
        <tr>
          <td
            style="padding:40px 36px 48px;font-family:Arial,Helvetica,sans-serif;color:#1e293b;background-color:#ffffff;">`,
  suffix: `</td>
        </tr>
      </table>
    </td>
  </tr>
</table>`,
};

/** Typography for editable body content — injected into the preview iframe. */
export const EMAIL_BODY_CONTENT_STYLES = `
  body {
    margin: 0;
    padding: 0;
    background-color: #eef2f7;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 15px;
    line-height: 1.55;
    color: #334155;
  }
  body, p, h1, h2, h3, h4, h5, h6, td, a, span, div, li {
    font-family: Arial, Helvetica, sans-serif !important;
  }

  /* Headings */
  h1 {
    font-size: 28px;
    margin: 0 0 18px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    line-height: 1.25;
  }
  h2 {
    font-size: 22px;
    margin: 0 0 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    line-height: 1.3;
  }
  h3 {
    font-size: 18px;
    margin: 0 0 12px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
  }
  h4 {
    font-size: 16px;
    margin: 0 0 10px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
  }
  h5 {
    font-size: 14px;
    margin: 0 0 8px;
    font-weight: 600;
    color: #0f172a;
    line-height: 1.3;
  }
  h6 {
    font-size: 12px;
    margin: 0 0 8px;
    font-weight: 600;
    color: #475569;
    line-height: 1.3;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  /* Paragraph */
  p {
    font-size: 15px;
    margin: 0 0 14px;
    line-height: 1.55;
    color: #334155;
  }

  /* Inline marks */
  strong, b { font-weight: 700; }
  em, i { font-style: italic; }
  u { text-decoration: underline; }
  s, del { text-decoration: line-through; }

  /* Links */
  a {
    color: #1e4d7b;
    text-decoration: underline;
    word-break: break-all;
  }

  /* .button class links used in .hbs templates */
  a.button {
    display: inline-block;
    padding: 12px 24px;
    background-color: #132E56 !important;
    color: #ffffff !important;
    text-decoration: none !important;
    border-radius: 5px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.5;
    word-break: normal;
  }
  a.button:hover {
    background-color: #1a3d72 !important;
  }

  /* Lists */
  ul {
    list-style-type: disc;
    margin: 0 0 14px;
    padding-left: 1.75rem;
  }
  ol {
    list-style-type: decimal;
    margin: 0 0 14px;
    padding-left: 1.75rem;
  }
  li {
    font-size: 15px;
    line-height: 1.55;
    color: #334155;
    margin-bottom: 5px;
  }
  li:last-child { margin-bottom: 0; }
  li > ul, li > ol { margin: 5px 0 0; }
  ul ul { list-style-type: circle; }
  ul ul ul { list-style-type: square; }

  /* Blockquote */
  blockquote {
    border-left: 3px solid #cbd5e1;
    margin: 0 0 14px;
    padding: 10px 16px;
    color: #64748b;
    font-style: italic;
    background: #f8fafc;
    border-radius: 0 4px 4px 0;
  }
  blockquote p { margin: 0; color: #64748b; }

  /* Code */
  code {
    font-family: 'Courier New', Courier, monospace;
    background-color: #f1f5f9;
    border-radius: 3px;
    padding: 2px 6px;
    font-size: 13px;
    color: #0f172a;
  }
  pre {
    background-color: #1e293b;
    border-radius: 6px;
    padding: 14px 16px;
    margin: 0 0 14px;
    overflow-x: auto;
  }
  pre code {
    background: none;
    padding: 0;
    border-radius: 0;
    font-size: 13px;
    color: #e2e8f0;
  }

  /* Image */
  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 8px auto;
  }

  /* Table */
  table { border-collapse: collapse; }

  /* Strip ngx-editor span highlight artifacts */
  td[style*="padding:40px"] h2 span,
  td[style*="padding:40px"] p span,
  td[style*="padding:40px"] a span {
    background-color: transparent !important;
  }

  /* Primary CTA button links (editor output with <strong> inside) */
  td[style*="padding:40px"] p > a:has(strong) {
    display: inline-block;
    padding: 14px 32px;
    background-color: #132E56 !important;
    color: #ffffff !important;
    text-decoration: none !important;
    border-radius: 10px;
    font-weight: 700;
    font-size: 15px;
    line-height: 1.5;
    margin: 8px 0 24px;
  }
  td[style*="padding:40px"] p > a:has(strong) strong,
  td[style*="padding:40px"] p > a:has(strong) span {
    color: #ffffff !important;
    background-color: transparent !important;
    text-decoration: none !important;
    font-weight: 700;
  }
`;

/** Injected into generated .hbs <head> so editor output renders correctly in mail clients. */
export const EMAIL_BODY_HEAD_STYLES = `<style type="text/css">
  h1 {
    font-size: 28px;
    margin: 0 0 18px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    line-height: 1.25;
  }
  h2 {
    font-size: 22px;
    margin: 0 0 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    line-height: 1.3;
  }
  h3 {
    font-size: 18px;
    margin: 0 0 12px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
  }
  h4 {
    font-size: 16px;
    margin: 0 0 10px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
  }
  h5 {
    font-size: 14px;
    margin: 0 0 8px;
    font-weight: 600;
    color: #0f172a;
  }
  h6 {
    font-size: 12px;
    margin: 0 0 8px;
    font-weight: 600;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  p {
    font-size: 15px;
    margin: 0 0 14px;
    line-height: 1.55;
    color: #334155;
  }
  a {
    color: #1e4d7b;
    word-break: break-all;
  }
  a.button {
    display: inline-block;
    padding: 12px 24px;
    background-color: #132E56 !important;
    color: #ffffff !important;
    text-decoration: none !important;
    border-radius: 5px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.5;
    word-break: normal;
  }
  strong, b { font-weight: 700; }
  ul {
    list-style-type: disc;
    margin: 0 0 14px;
    padding-left: 1.5rem;
  }
  ol {
    list-style-type: decimal;
    margin: 0 0 14px;
    padding-left: 1.5rem;
  }
  li {
    font-size: 15px;
    line-height: 1.55;
    color: #334155;
    margin-bottom: 5px;
  }
  blockquote {
    border-left: 3px solid #cbd5e1;
    margin: 0 0 14px;
    padding: 10px 16px;
    color: #64748b;
    font-style: italic;
  }
  code {
    font-family: 'Courier New', Courier, monospace;
    background-color: #f1f5f9;
    border-radius: 3px;
    padding: 2px 6px;
    font-size: 13px;
    color: #0f172a;
  }
  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 8px auto;
  }
  td[style*="padding:40px"] h2 span,
  td[style*="padding:40px"] p span,
  td[style*="padding:40px"] a span {
    background-color: transparent !important;
  }
  td[style*="padding:40px"] p > a:has(strong) {
    display: inline-block;
    padding: 14px 32px;
    background-color: #132E56 !important;
    color: #ffffff !important;
    text-decoration: none !important;
    border-radius: 10px;
    font-weight: 700;
    font-size: 15px;
    line-height: 1.5;
    margin: 8px 0 24px;
  }
  td[style*="padding:40px"] p > a:has(strong) strong,
  td[style*="padding:40px"] p > a:has(strong) span {
    color: #ffffff !important;
    background-color: transparent !important;
    text-decoration: none !important;
    font-weight: 700;
  }
</style>`;

/** From `partials/email-header.hbs` */
export const EMAIL_HEADER_PARTIAL = `<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:#eef2f7;">
  <tr>
    <td align="center" style="padding:28px 16px 0;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600"
        style="max-width:600px;width:100%;border-collapse:collapse;">
        <tr>
          <td style="border-radius:18px 18px 0 0;background-color:#EDE0CC;padding:16px 24px;text-align:left;">
            <img
              src="{{logo}}"
              alt="{{website_name}}" width="180" height="60"
              style="display:block;max-width:180px;height:60px;object-fit:contain;border:0;outline:none;text-decoration:none;">
          </td>
        </tr>
        <tr>
          <td style="height:3px;background-color:#132E56;font-size:0;line-height:0;mso-line-height-rule:exactly;">
            &nbsp;</td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;

/** From `partials/email-footer.hbs` (preview always shows support line). */
export const EMAIL_FOOTER_PARTIAL = `<table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-color:#eef2f7;">
  <tr>
    <td align="center" style="padding:0 16px 36px;">
      <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="600" style="max-width:600px;width:100%;border-collapse:collapse;">
        <tr>
          <td style="border-radius:0 0 18px 18px;background-color:#EDE0CC;padding:15px 24px;text-align:center;font-family:Arial,Helvetica,sans-serif;">
            <p style="margin:0 0 8px;font-size:13px;color:#555555;line-height:1.5;">
              Questions? Reply to us at
              <a href="mailto:{{support_email}}" style="color:#132E56;text-decoration:underline;font-weight:600;word-break:break-all;">{{support_email}}</a>
            </p>
            <p style="margin:0;font-size:12px;color:#777777;line-height:1.6;">
              &copy; {{year}} {{website_name}}. All rights reserved.
            </p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>`;
